/**
 * Limits and tunables shared by client and server.
 *
 * Several of these are dictated by the Cloudflare free plan rather than by
 * product preference; those are marked, so a future reader knows which numbers
 * are negotiable and which are physics.
 */

export const API_VERSION = 'v1';
export const API_BASE_PATH = '/api/v1';

// ------------------------------------------------------------------ auth
export const ACCESS_TOKEN_TTL_SECONDS = 15 * 60; // 15 minutes
export const REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60; // 30 days ("remember me")
export const REFRESH_TOKEN_SHORT_TTL_SECONDS = 12 * 60 * 60; // 12 hours (shared computer)
export const PASSWORD_RESET_TTL_SECONDS = 30 * 60;

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** Failed attempts before an account+IP pair is locked. */
export const LOGIN_MAX_ATTEMPTS = 5;
export const LOGIN_LOCKOUT_SECONDS = 15 * 60;

/**
 * Cookie names carry security prefixes the browser itself enforces:
 *
 *   `__Host-`   requires Secure, Path=/ and NO Domain attribute. A subdomain
 *               cannot set or overwrite it, which blocks session fixation from a
 *               compromised sibling host.
 *   `__Secure-` requires Secure but permits a narrower Path.
 *
 * The refresh token uses `__Secure-` precisely because it needs a narrow path —
 * `__Host-` would forbid that, and scoping the refresh token to the auth
 * endpoints means it is not attached to the hundreds of ordinary API calls a
 * session makes, shrinking its exposure.
 *
 * Both prefixes require Secure, which browsers grant on http://localhost because
 * localhost is a trustworthy origin. Development therefore uses the same cookie
 * flags as production — no "works locally, breaks deployed" class of auth bug.
 */
export const COOKIE_ACCESS_TOKEN = '__Host-tf_at';
export const COOKIE_REFRESH_TOKEN = '__Secure-tf_rt';
export const COOKIE_CSRF = '__Host-tf_csrf';
/** Path the refresh cookie is scoped to. */
export const REFRESH_COOKIE_PATH = `${API_BASE_PATH}/auth`;
/** Short-lived cookie holding OAuth state + PKCE verifier during the redirect. */
export const COOKIE_OAUTH_STATE = '__Host-tf_oauth';
export const OAUTH_STATE_TTL_SECONDS = 10 * 60;
export const HEADER_CSRF = 'X-CSRF-Token';
export const HEADER_REQUEST_ID = 'X-Request-Id';
export const HEADER_IDEMPOTENCY_KEY = 'Idempotency-Key';

// ------------------------------------------------------------ rate limiting
/**
 * Per-user rate limits.
 *
 * `mutation` is 120/minute rather than 60. A kanban board is bursty by nature: somebody
 * reorganising a board drags twenty cards, retitles a few and archives a column inside
 * a minute or two, and 60/min (one per second sustained) would refuse a legitimate
 * cleanup session. 120 still stops a runaway client loop dead — that would attempt
 * thousands — and the real ceiling on abuse is the 100,000 requests/day free-tier
 * budget, not this.
 *
 * `auth` stays deliberately tight: those are the endpoints worth grinding, and the
 * durable lockout in `login_history` backs it up.
 */
export const RATE_LIMITS = {
  auth: { limit: 5, windowSeconds: 15 * 60 },
  mutation: { limit: 120, windowSeconds: 60 },
  read: { limit: 300, windowSeconds: 60 },
  upload: { limit: 20, windowSeconds: 60 * 60 },
} as const;

export type RateLimitTier = keyof typeof RATE_LIMITS;

// ------------------------------------------------------------------ content
export const LIMITS = {
  boardNameMax: 120,
  boardDescriptionMax: 4000,
  listNameMax: 120,
  cardTitleMax: 500,
  cardDescriptionMax: 20_000,
  commentMax: 10_000,
  checklistNameMax: 200,
  checklistItemMax: 500,
  labelNameMax: 60,
  userNameMax: 120,

  listsPerBoard: 50,
  cardsPerList: 2000,
  checklistsPerCard: 20,
  itemsPerChecklist: 100,
  labelsPerBoard: 30,
  attachmentsPerCard: 25,
} as const;

// ------------------------------------------------------------------ pagination
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

// ------------------------------------------------------------------ uploads
/**
 * 10 MB per file. Deliberately conservative: the R2 free allowance is 10 GB
 * total, and per Akash's brief this is for images and light documents, not
 * heavy media.
 */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** Workspace storage ceiling — 8 GB, leaving headroom inside R2's free 10 GB. */
export const WORKSPACE_STORAGE_QUOTA_BYTES = 8 * 1024 * 1024 * 1024;

export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/avif',
] as const;

export const ALLOWED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export const ALLOWED_UPLOAD_MIME_TYPES = [
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_DOCUMENT_MIME_TYPES,
] as const;

/**
 * SVG is intentionally absent. An SVG is an executable document; serving one
 * from our own origin would be a stored-XSS vector.
 */

// ------------------------------------------------------------ free-tier budget
/**
 * Cloudflare free plan ceilings. A cron job alerts when usage crosses
 * ALERT_THRESHOLD so we learn about pressure before users do.
 */
export const FREE_TIER_BUDGET = {
  workerRequestsPerDay: 100_000,
  d1RowsReadPerDay: 5_000_000,
  d1RowsWrittenPerDay: 100_000,
  kvWritesPerDay: 1_000,
  r2StorageBytes: 10 * 1024 * 1024 * 1024,
} as const;

export const BUDGET_ALERT_THRESHOLD = 0.8;

// ------------------------------------------------------------------ realtime
export const REALTIME_RECONNECT_BASE_MS = 1000;
export const REALTIME_RECONNECT_MAX_MS = 30_000;
/** Replay window: how many board events we keep for reconnect catch-up. */
export const BOARD_EVENT_RETENTION_DAYS = 7;
